import React, { useState, useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import {
  fetchDashboardRevenueStats,
  fetchDashboardPaymentStats,
} from "../../services/dashboardService";

const DashboardCharts = () => {
  const [revenueData, setRevenueData] = useState({
    saleAmount: [],
    paidAmount: [],
    dayMonthNYear: [],
    totalOrder: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalPending: 0,
    totalWaiveOff: 0,
  });

  const [paymentStatus, setPaymentStatus] = useState([]);
  const [activeRevenueFilter, setActiveRevenueFilter] = useState("1Y");
  const [activeStatusFilter, setActiveStatusFilter] = useState("1Y");
  const [loading, setLoading] = useState(true);

  const revenueChartRef = useRef(null);
  const paymentChartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const paymentChartInstanceRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Fetch revenue data (default 1Y)
      const revenueResponse = await fetchDashboardRevenueStats("1Y");
      if (revenueResponse.isSuccess) {
        console.log("Revenue Data:", revenueResponse.result);
        setRevenueData(revenueResponse.result);
      }

      // Fetch payment status data
      const paymentResponse = await fetchDashboardPaymentStats("1Y");
      if (paymentResponse.isSuccess) {
        const result = paymentResponse.result || {};

        const statusArray = [
          result.totalInitiated || 0,
          result.totalPending || 0,
          result.totalPaid || 0,
          result.totalFailed || 0,
        ];

        setPaymentStatus(statusArray);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize/Update Revenue Chart
  useEffect(() => {
    if (
      !loading &&
      revenueChartRef.current &&
      revenueData.dayMonthNYear.length > 0
    ) {
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initRevenueChart();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [revenueData, loading, activeRevenueFilter]); // Added activeRevenueFilter as dependency

  // Initialize/Update Payment Chart
  useEffect(() => {
    if (!loading && paymentChartRef.current && paymentStatus.length > 0) {
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initPaymentChart();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [paymentStatus, loading, activeStatusFilter]); // Added activeStatusFilter as dependency

  const initRevenueChart = () => {
    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.destroy();
      } catch (error) {
        console.warn("Error destroying chart:", error);
      }
      chartInstanceRef.current = null;
    }

    // Ensure the container exists and has valid data
    if (!revenueChartRef.current || revenueData.dayMonthNYear.length === 0) {
      return;
    }

    console.log("Initializing revenue chart with data:", revenueData);

    const options = {
      series: [
        {
          name: "Confirmed Sale",
          type: "area",
          data: revenueData.saleAmount || [],
        },
        {
          name: "Paid Amount",
          type: "area",
          data: revenueData.paidAmount || [],
        },
      ],
      chart: {
        height: 370,
        type: "line",
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      stroke: {
        curve: "straight",
        dashArray: [0, 0, 8],
        width: [2, 0, 2.2],
      },
      fill: {
        opacity: [0.1, 0.2],
      },
      markers: {
        size: [0, 0, 0],
        strokeWidth: 2,
        hover: { size: 4 },
      },
      xaxis: {
        categories: revenueData.dayMonthNYear || [],
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      grid: {
        show: true,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: {
          top: 0,
          right: -2,
          bottom: 15,
          left: 10,
        },
      },
      legend: {
        show: true,
        horizontalAlign: "center",
        offsetX: 0,
        offsetY: -5,
        markers: { width: 9, height: 9, radius: 6 },
        itemMargin: { horizontal: 10, vertical: 0 },
      },
      plotOptions: {
        bar: { columnWidth: "30%", barHeight: "70%" },
      },
      colors: ["#45CB85", "#f06548"],
      tooltip: {
        shared: true,
        y: [
          {
            formatter: function (value) {
              return value !== undefined ? `₹${value.toFixed(2)}` : value;
            },
          },
        ],
      },
    };

    try {
      chartInstanceRef.current = new ApexCharts(
        revenueChartRef.current,
        options
      );
      chartInstanceRef.current.render();
    } catch (error) {
      console.error("Error creating revenue chart:", error);
    }
  };

  const initPaymentChart = () => {
    // Destroy existing chart if it exists
    if (paymentChartInstanceRef.current) {
      try {
        paymentChartInstanceRef.current.destroy();
      } catch (error) {
        console.warn("Error destroying payment chart:", error);
      }
      paymentChartInstanceRef.current = null;
    }

    // Ensure the container exists and has valid data
    if (!paymentChartRef.current || paymentStatus.length === 0) {
      return;
    }

    const options = {
      series: paymentStatus,
      labels: ["Initiated", "Pending", "Success", "Failed"],
      chart: {
        height: 333,
        type: "donut",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      legend: {
        position: "bottom",
      },
      stroke: {
        show: false,
      },
      dataLabels: {
        dropShadow: { enabled: false },
      },
      colors: ["#4b38b3", "#ffbe0b", "#45CB85", "#f06548"],
    };

    try {
      paymentChartInstanceRef.current = new ApexCharts(
        paymentChartRef.current,
        options
      );
      paymentChartInstanceRef.current.render();
    } catch (error) {
      console.error("Error creating payment chart:", error);
    }
  };

  const handleRevenueFilter = async (filterValue) => {
    if (filterValue === activeRevenueFilter) return;

    try {
      setActiveRevenueFilter(filterValue);

      // Temporarily clear the chart to show loading state
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const response = await fetchDashboardRevenueStats(filterValue);

      if (response.isSuccess) {
        setRevenueData(response.result);
      }
    } catch (error) {
      console.error("Error filtering revenue data:", error);
    }
  };

  const handleStatusFilter = async (filterValue) => {
    if (filterValue === activeStatusFilter) return;

    try {
      setActiveStatusFilter(filterValue);

      // Temporarily clear the chart to show loading state
      if (paymentChartInstanceRef.current) {
        paymentChartInstanceRef.current.destroy();
        paymentChartInstanceRef.current = null;
      }

      const response = await fetchDashboardPaymentStats(filterValue);

      if (response.isSuccess) {
        const result = response.result || {};

        const statusArray = [
          result.totalInitiated || 0,
          result.totalPending || 0,
          result.totalPaid || 0,
          result.totalFailed || 0,
        ];

        setPaymentStatus(statusArray);
      }
    } catch (error) {
      console.error("Error filtering payment status:", error);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${Math.floor(amount).toLocaleString()}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying chart on cleanup:", error);
        }
      }
      if (paymentChartInstanceRef.current) {
        try {
          paymentChartInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying payment chart on cleanup:", error);
        }
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-8">
        <div className="card">
          <div className="card-header border-0 align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">
              Revenue{" "}
              <span className="fs-12 text-success">
                (Total Order :{" "}
                <span className="fs-12">{revenueData.totalOrder}</span>)
              </span>
            </h4>
            <div>
              {["All", "1W", "1M", "6M", "1Y"].map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`btn btn-sm shadow-none me-1 ${
                    activeRevenueFilter === filter
                      ? "btn-soft-primary selected"
                      : "btn-soft-secondary"
                  }`}
                  onClick={() => handleRevenueFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div className="card-header p-0 border-0 bg-soft-light">
            <div className="row g-0 text-center">
              <div className="col-6 col-sm-4">
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    {formatCurrency(revenueData.totalAmount)}
                  </h5>
                  <p className="text-muted mb-0">Total Amount</p>
                </div>
              </div>
              <div className="col-6 col-sm-4">
                <div className="p-3 border border-dashed border-start-0">
                  <h5 className="mb-1">
                    {formatCurrency(revenueData.totalPaid)}
                  </h5>
                  <p className="text-muted mb-0">Paid Amount</p>
                </div>
              </div>
              <div className="col-6 col-sm-4">
                <div className="p-3 border border-dashed border-start-0 border-end-0">
                  <h5 className="mb-1">
                    {formatCurrency(revenueData.totalPending)}
                  </h5>
                  <p className="text-muted mb-0">Pending Amount</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body p-0 pb-2">
            <div className="w-100">
              <div
                ref={revenueChartRef}
                id="customer_impression_charts"
                className="apex-charts"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-4">
        <div className="card card-height-100">
          <div className="card-header align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">Payment Status</h4>
            <div>
              {["All", "1W", "1M", "6M", "1Y"].map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`btn btn-sm shadow-none me-1 ${
                    activeStatusFilter === filter
                      ? "btn-soft-primary selected"
                      : "btn-soft-secondary"
                  }`}
                  onClick={() => handleStatusFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div className="card-body">
            <div
              ref={paymentChartRef}
              id="store-visits-source"
              className="apex-charts"
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;
